import React, { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './FollowDrawer.css';
import closeIcon from '../assets/close-icon.png';
import defaultProfile from '../assets/placeholder-profile.png';
import goldTickIcon from '../assets/goldtick-icon.png';

function FollowDrawer({ isOpen, onClose, title, list = [] }) {
  const navigate = useNavigate();

  const pushedRef = useRef(false);
  const closingFromButtonRef = useRef(false);

  const isAdminUser = (user) => user?.isAdmin === true || !!user?.adminRole;
  const isAppUser = (user) => user?.isApp === true || user?.accountType === 'app';
  const isSpecialUser = (user) => isAdminUser(user) || isAppUser(user);

  const getSpecialTitle = (user) => {
    if (isAdminUser(user)) return user?.adminRole || 'Admin';
    if (isAppUser(user)) return 'App account';
    return '';
  };

  // =========================
  // Dynamic Drawer Height
  // =========================
  const drawerSizeClass =
    list.length >= 6
      ? 'follow-drawer-large'
      : list.length <= 2
      ? 'follow-drawer-compact'
      : 'follow-drawer-medium';

  // ✅ normal close for overlay / close button
  const safeClose = useCallback(({ manual }) => {
    if (manual) closingFromButtonRef.current = true;

    try {
      onClose?.();
    } catch {}

    if (
      manual &&
      pushedRef.current &&
      window.history.state?.bfDrawer === 'follow'
    ) {
      try {
        window.history.back();
      } catch {}

      pushedRef.current = false;
    }
  }, [onClose]);

  // ✅ special close used when clicking a user in the drawer
  // this avoids history.back() fighting with navigate()
  const closeForNavigation = useCallback(() => {
    try {
      onClose?.();
    } catch {}

    closingFromButtonRef.current = false;
    pushedRef.current = false;
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return;

    try {
      window.history.pushState({ bfDrawer: 'follow', title }, '');
      pushedRef.current = true;
    } catch {}

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onPopState = () => {
      if (closingFromButtonRef.current) {
        closingFromButtonRef.current = false;
        return;
      }

      try {
        onClose?.();
      } catch {}

      pushedRef.current = false;
    };

    window.addEventListener('popstate', onPopState);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('popstate', onPopState);

      if (
        pushedRef.current &&
        window.history.state?.bfDrawer === 'follow'
      ) {
        try {
          window.history.back();
        } catch {}

        pushedRef.current = false;
      }
    };
  }, [isOpen, onClose, title]);

  if (!isOpen) return null;

  return (
    <div
      className="follow-drawer-overlay"
      onClick={() => safeClose({ manual: true })}
    >
      <div
        className={`follow-drawer ${drawerSizeClass}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="follow-drawer-header">
          <h3>{title}</h3>

          <img
            src={closeIcon}
            alt="close"
            onClick={() => safeClose({ manual: true })}
          />
        </div>

        <div className="follow-drawer-body">
          {list.length === 0 ? (
            <p className="follow-empty">No users to display.</p>
          ) : (
            list.map((user, i) => {
              const src = user?.profilePic || defaultProfile;

              return (
                <div
                  key={user?._id || user?.username || i}
                  className="drawer-user-card"
                  onClick={() => {
                    if (!user?.username) return;

                    closeForNavigation();
                    navigate(`/user/${user.username}`);
                  }}
                >
                  <img
                    src={src}
                    alt="avatar"
                    className="drawer-avatar"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = defaultProfile;
                    }}
                  />

                  <div className="drawer-user-info">
                    <div className="drawer-name">
                      {user?.name}
                    </div>

                    <div className="drawer-username">
                      <span className="drawer-username-row">
                        <span>@{user?.username}</span>

                        {isSpecialUser(user) && (
                          <img
                            src={goldTickIcon}
                            alt={
                              isAdminUser(user)
                                ? 'Admin verified'
                                : 'App verified'
                            }
                            className="drawer-gold-tick"
                            title={getSpecialTitle(user)}
                          />
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

export default FollowDrawer;
